/**
 * Data providers for mention suggestion lists.
 * Queries the backend API for users, tickets, reports, and vehicles.
 */
import { PartnerModule } from "@/store/modules/partner";
import ticketService from "@/services/mrfiktiv/services/ticketService";
import reportService from "@/services/mrfiktiv/services/reportService";
import vehicleService from "@/services/mrfiktiv/services/vehicleService";
import partnerUserService from "@/services/shared/partnerUserService";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerUser } from "@/models/user.entity";
import { SuggestionItem } from "./suggestion-renderer";

function getPartnerId(): string {
  return PartnerModule.partner._id || PartnerModule.partner.id || "";
}

/**
 * Fetch user suggestions matching the query string via backend API.
 *
 * Archived users (those whose only permission for the partner is PARTNER:read)
 * are flagged so they can be de-emphasized in the suggestion list. The flag is
 * derived from the permission data already returned by `findAll` — reusing the
 * shared `PartnerUser.isArchived` getter — so no extra per-user status lookups
 * are needed.
 */
export async function fetchUserSuggestions(query: string): Promise<SuggestionItem[]> {
  const partnerId = getPartnerId();
  if (!partnerId) return [];

  try {
    const res = await partnerUserService.findAll({
      partnerId,
      search: query || undefined,
      itemsPerPage: 8
    });

    const users = (res.data || []).filter((u) => u.id && (u.firstName || u.lastName || u.userName));

    return users.map((user) => ({
      id: user.id,
      label: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.userName || "",
      icon: "mdi-account",
      isArchived: new PartnerUser(user).isArchived
    }));
  } catch {
    return [];
  }
}

/**
 * Fetch reference suggestions (tickets, reports, vehicles) matching the query
 * via backend API. Searches all three entity types in parallel.
 */
export async function fetchRefSuggestions(query: string): Promise<SuggestionItem[]> {
  const partnerId = getPartnerId();
  if (!partnerId) return [];

  const searchParam = query || undefined;

  const canReadTickets = UserModule.abilities.can(ActionEnum.READ, BackendResourceEnum.TICKET, partnerId);
  const canReadReports = UserModule.abilities.can(ActionEnum.READ, BackendResourceEnum.REPORT, partnerId);
  const canReadVehicles = UserModule.abilities.can(ActionEnum.READ, BackendResourceEnum.VEHICLE, partnerId);

  const [ticketResult, reportResult, vehicleResult] = await Promise.allSettled([
    canReadTickets
      ? ticketService.getAll({ partnerId, search: searchParam, itemsPerPage: 5 })
      : Promise.resolve({ data: [] }),
    canReadReports
      ? reportService.getReportPaginated({ partnerId, search: searchParam, itemsPerPage: 5 })
      : Promise.resolve({ data: [] }),
    canReadVehicles
      ? vehicleService.getAll({ partnerId, search: searchParam, itemsPerPage: 5 })
      : Promise.resolve({ data: [] })
  ]);

  const results: SuggestionItem[] = [];

  if (ticketResult.status === "fulfilled" && ticketResult.value?.data) {
    for (const ticket of ticketResult.value.data) {
      results.push({
        id: ticket.id,
        label: ticket.title || `#${ticket.number}` || ticket.id,
        refType: BackendResourceEnum.TICKET,
        icon: "mdi-check-circle-outline",
        subtitle: `#${ticket.number}`
      });
    }
  }

  if (reportResult.status === "fulfilled" && reportResult.value?.data) {
    for (const report of reportResult.value.data) {
      results.push({
        id: report.id,
        label: report.title || report.claimNumber || report.id,
        refType: BackendResourceEnum.REPORT,
        icon: "mdi-car-wrench",
        subtitle: report.numberplate || undefined
      });
    }
  }

  if (vehicleResult.status === "fulfilled" && vehicleResult.value?.data) {
    for (const vehicle of vehicleResult.value.data) {
      results.push({
        id: vehicle.id,
        label: vehicle.numberplate || vehicle.displayName || "",
        refType: BackendResourceEnum.VEHICLE,
        icon: "mdi-car",
        subtitle: vehicle.displayName || undefined
      });
    }
  }

  return results.filter((item) => item.id && item.label);
}
